import { useState } from "react";
import { Button, ButtonVariant, Text, TextField, Toggle } from "@merge-api/merge-javascript-shared";
import { Check } from "lucide-react";
import { Playbook, PlaybookSummary } from "../types";
import { showErrorToast, showSuccessToast } from "../../shared/Toasts";
import { conductorPost } from "../conductorApi";
import ConductorDrawer from "./ConductorDrawer";
import { FieldLabel, Section, Select } from "./ConductorFormFields";

interface Props {
  integrationId: string;
  /** Available categories for the integration. When >1, the user must pick one. */
  categories: string[];
  existingPlaybooks: PlaybookSummary[];
  onClose: () => void;
  onCreated: (playbookId: string | null) => void;
}

// Creates an (entry-less) playbook for the integration. Entries are attached
// afterward from the playbook detail view. Scope (org / linked account) is
// optional; blank means the playbook matches all at that level.
const PlaybookEditorDrawer = ({
  integrationId,
  categories,
  existingPlaybooks,
  onClose,
  onCreated,
}: Props) => {
  const resolvedCategory = categories.length === 1 ? categories[0] : undefined;
  const needsCategoryChoice = !resolvedCategory && categories.length > 1;

  const [selectedCategory, setSelectedCategory] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [linkedAccountId, setLinkedAccountId] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Conductor resolves playbooks by scope and has no pure-create: posting a
  // scope that already exists upserts (and overwrites) that playbook. Block it.
  const orgId = organizationId.trim() || null;
  const linkedId = linkedAccountId.trim() || null;
  const effectiveCategory = resolvedCategory ?? selectedCategory;
  const scopeTaken = existingPlaybooks.some(
    (pb) =>
      (pb.category ?? "") === (effectiveCategory ?? "") &&
      (pb.organization_id ?? null) === orgId &&
      (pb.linked_account_id ?? null) === linkedId
  );
  const canSave = !scopeTaken && !!effectiveCategory && !isSaving;

  const save = async () => {
    if (scopeTaken) return;
    setIsSaving(true);
    const { ok, data } = await conductorPost<Playbook>("/conductor/playbooks", {
      integration_id: integrationId,
      organization_id: orgId,
      linked_account_id: linkedId,
      category: effectiveCategory,
      enabled,
      entries: [],
    });
    setIsSaving(false);
    if (ok) {
      showSuccessToast("Playbook created");
      onCreated(data?.id ?? null);
    } else {
      showErrorToast("Failed to create playbook");
    }
  };

  return (
    <ConductorDrawer
      title="New playbook"
      onClose={onClose}
      footer={
        <>
          <span />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={ButtonVariant.SecondaryGray}
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant={ButtonVariant.PrimaryGray}
              leftIcon={<Check size={14} />}
              onClick={save}
              disabled={!canSave}
            >
              Create playbook
            </Button>
          </div>
        </>
      }
    >
      <Section title="Scope">
        <Text variant="sm" className="mb-4 block text-gray-50">
          A playbook is resolved for a linked account by integration, then organization, then linked
          account. Leave a field blank to match all at that level.
        </Text>
        <div className="mb-4">
          <FieldLabel>Category</FieldLabel>
          {needsCategoryChoice ? (
            <Select value={selectedCategory} onChange={setSelectedCategory}>
              <option value="">Select a category…</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </Select>
          ) : (
            <Text variant="md" className="font-mono text-gray-80">
              {effectiveCategory || "-"}
            </Text>
          )}
        </div>
        <div className="mb-4">
          <FieldLabel>Organization ID</FieldLabel>
          <TextField
            className="bg-white"
            value={organizationId}
            onChange={(e) => setOrganizationId(e.target.value)}
            placeholder="blank = all organizations"
          />
        </div>
        <div className="mb-4">
          <FieldLabel>Linked account ID</FieldLabel>
          <TextField
            className="bg-white"
            value={linkedAccountId}
            onChange={(e) => setLinkedAccountId(e.target.value)}
            placeholder="blank = all linked accounts"
          />
        </div>
        {scopeTaken && (
          <Text variant="sm" className="mb-4 block text-red-60">
            A playbook with this scope already exists. Open it from the list to edit it, instead of
            creating a duplicate (which would overwrite it).
          </Text>
        )}
        <div className="flex items-center justify-between">
          <FieldLabel>Enabled</FieldLabel>
          <Toggle checked={enabled} onChange={setEnabled} />
        </div>
      </Section>
    </ConductorDrawer>
  );
};

export default PlaybookEditorDrawer;
